import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import { useAppTFunction } from '@retail/app/i18n';
import {
  useCustomerAssortments,
  usePermissionsForCurrentUser,
} from '@retail/app/stores/selected-context';
import { CreateCustomerDialog, CreateCustomerSchema } from '@retail/customer-discount/components';
import { useCreateCustomerAssortment } from '@retail/customer-discount/data-access';
import { useToast } from '@retail/hooks';
import { customerAssortmentIdSearchParam } from '@retail/utils';
import { ContainedButton } from '@shared/components';
import { useDisclosure } from '@shared/hooks';

export function CreateCustomerContainer() {
  const t = useAppTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const { userCanCreate } = usePermissionsForCurrentUser();
  const customerAssortments = useCustomerAssortments();
  const dialog = useDisclosure();
  const [nameError, setNameError] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();

  const { mutateAsync: createCustomerAssortment, isPending } = useCreateCustomerAssortment();

  if (!userCanCreate('DISCOUNT_CUSTOMERS')) return null;

  const clearErrors = () => {
    setNameError(undefined);
    setSubmitError(undefined);
  };

  const handleClose = () => {
    clearErrors();
    dialog.onClose();
  };

  const handleSubmit = ({
    name,
    discountMatrixTemplateAssortmentId,
    assortmentTemplateAssortmentId,
  }: CreateCustomerSchema) => {
    clearErrors();
    createCustomerAssortment({
      body: {
        name,
        discountMatrixTemplateAssortmentId: discountMatrixTemplateAssortmentId ?? undefined,
        assortmentTemplateAssortmentId: assortmentTemplateAssortmentId ?? undefined,
      },
    })
      .then(({ id }) => {
        queryClient.invalidateQueries({ queryKey: ['customer-assortments'] });
        queryClient.invalidateQueries({ queryKey: ['my-assortments'] });
        setSearchParams(
          (prevParams) => {
            const nextParams = new URLSearchParams(prevParams);
            nextParams.set(customerAssortmentIdSearchParam, String(id));
            return nextParams;
          },
          { replace: true }
        );
        toast.success(t('app.createCustomer.success', { name }));
        dialog.onClose();
      })
      .catch((err) => {
        if (err.status === 409) {
          setNameError(t('app.createCustomer.error.nameTaken'));
        } else {
          console.error(err);
          setSubmitError(t('app.createCustomer.error.generic'));
        }
      });
  };

  return (
    <>
      <ContainedButton
        startIcon={<AddIcon fontSize="small" />}
        size="small"
        color="secondary"
        onClick={dialog.onOpen}
      >
        {t('app.createCustomer.title')}
      </ContainedButton>
      <CreateCustomerDialog
        open={dialog.isOpen}
        customerAssortments={customerAssortments}
        isSubmitting={isPending}
        nameError={nameError}
        submitError={submitError}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </>
  );
}
